import { emptyObject } from '@constants/empty';
import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import { getImage } from '@utils/imageHelper';
import isEmptyObject from '@utils/isEmptyObject';
import React, { useEffect, useRef, useState } from 'react';
import Link from '@base/Link/Link';
import Image from '@base/Image';
import { segment } from '@actions/analytics';
import { ACHIEVERS_CAROUSEL_CARD_CLICKED } from '@constants/segment';
import { ArrowButton } from 'aquilla/core/molecules/Carousel';
import useWindowSize from '@hooks/useWindowSize';
import useGoalEventProps from '@hooks/useGoalEventProps';

const SecContainer = styled.div`
  width: var(--layout-width);
  border-radius: ${Spacings.SPACING_4B};
  background-color: var(--color-base-1);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: url(${getImage('achieversCard/web_mainbg.svg')});
  padding: ${Spacings.SPACING_11B} 0;

  ${mediaQuery.uptoTablet} {
    width: 100%;
    border-radius: 0;
    padding: ${Spacings.SPACING_8B} 0;
  }
`;

const CarouselContainer = styled.div`
  width: var(--layout-width - 44px);
  overflow: hidden;
  position: relative;
  overscroll-behavior: unset;
  ::-webkit-scrollbar {
    display: none;
  }
  ${mediaQuery.uptoTablet} {
    width: 100%;
    overflow: scroll;
  }
`;

const CarouselWrapper = styled.div`
  display: flex;
  transition: transform linear 300ms;
`;

const CarouselItem = styled.div`
  flex-shrink: 0;

  :first-of-type {
    padding-left: ${Spacings.SPACING_11B};
  }
  :last-of-type {
    padding-right: ${Spacings.SPACING_11B};
  }

  ${mediaQuery.uptoMobile} {
    :first-of-type {
      padding-left: ${Spacings.SPACING_4B};
    }
    :last-of-type {
      padding-right: ${Spacings.SPACING_4B};
    }
  }
`;
const CardWapper = styled.div`
  position: relative;
  width: 472px;
  height: 328px;
  background-color: var(--color-i-orange-6);
  border-radius: ${Spacings.SPACING_3B};
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  background-image: ${({ $src }) => `url(${getImage($src)})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  margin-right: ${Spacings.SPACING_5B};
  ${mediaQuery.uptoTablet} {
    margin-right: ${Spacings.SPACING_3B};
    width: unset;
    height: 260px;
    aspect-ratio: 472/328;
  }
  ${mediaQuery.uptoMobile} {
    margin-right: ${Spacings.SPACING_3B};
    width: 320px;
    height: 320px;
  }
`;

const StyledArrowButton = styled(ArrowButton)`
  z-index: 1;
  position: absolute;
  top: calc(50% - ${(props) => props.theme.measurements.spacing(8)});
  left: ${(props) =>
    props.type === 'prev' ? `${Spacings.SPACING_0B}` : 'initial'};
  right: ${(props) =>
    props.type === 'next' ? `${Spacings.SPACING_0B}` : 'initial'};
  ${mediaQuery.uptoTablet} {
    display: none;
  }
`;

const AchieversCard = ({ block, goalUID }) => {
  const { data = [] } = block;

  const { isMobile } = useWindowSize();
  const goalSegmentData = useGoalEventProps({ goalUID });
  const carouselRef = useRef(null);
  const [currItemIndex, setIndex] = useState(1);
  const showPrevButton = currItemIndex !== 1;
  const showNextButton = currItemIndex !== data.length - 1;
  useEffect(() => {
    carouselRef.current.style.transform = `translateX(calc(-${
      currItemIndex - 1
    } * (472px + 20px)))`;
  }, [currItemIndex]);
  const handleCardClick = (ctaLink, segmentData) => {
    segment.track(ACHIEVERS_CAROUSEL_CARD_CLICKED, {
      ...segmentData
    });
    window.open(ctaLink, '_blank');
  };

  const renderCardComponts = (cards) => {
    return cards.map((card) => {
      // eslint-disable-next-line no-shadow
      const { type, data, extraInfo } = card;
      const cardData = data?.[0] ?? emptyObject;
      const imgSrc = isMobile ? 'mobile' : 'web';
      let icon;
      if (cardData.icon) {
        icon = cardData.icon;
      } else {
        const { webImage, mwebImage } = cardData;
        icon = imgSrc === 'web' ? webImage : mwebImage;
      }
      const { ctaLink } = extraInfo ? extraInfo.ctaDetails : {};
      if (!isEmptyObject(card)) {
        return (
          <CarouselItem key={ctaLink}>
            <CardWapper $src={`achieversCard/${imgSrc}_${type}.svg`}>
              {ctaLink ? (
                <Link
                  disabled
                  href={ctaLink}
                  onClick={() =>
                    handleCardClick(ctaLink, {
                      ...goalSegmentData,
                      card_type: type
                    })
                  }
                >
                  <Image src={icon} layout="fill" alt="Achiever-icon" />
                </Link>
              ) : (
                <Image src={icon} layout="fill" alt="Achiever-icon" />
              )}
            </CardWapper>
          </CarouselItem>
        );
      }
      return null;
    });
  };

  const handleNext = () => {
    setIndex(currItemIndex + 1);
  };

  const handlePrev = () => {
    setIndex(currItemIndex - 1);
  };

  return (
    <SecContainer>
      <CarouselContainer>
        {showPrevButton && (
          <StyledArrowButton type="prev" onClick={handlePrev} />
        )}
        <CarouselWrapper ref={carouselRef}>
          {renderCardComponts(data)}
        </CarouselWrapper>
        {showNextButton && (
          <StyledArrowButton type="next" onClick={handleNext} />
        )}
      </CarouselContainer>
    </SecContainer>
  );
};

export default AchieversCard;
